import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Sparkles, Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

const RISK = {
  laag: { ring: "#10B981", labelKey: "demo.result.safe", chip: "bg-success/15 text-success" },
  gemiddeld: { ring: "#F59E0B", labelKey: "demo.result.caution", chip: "bg-warning/15 text-warning" },
  hoog: { ring: "#EF4444", labelKey: "demo.result.danger", chip: "bg-danger/15 text-danger" },
};

const TrustRing = ({ score, color }) => {
  const r = 52;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  return (
    <div className="relative h-32 w-32">
      <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#E2E8F0" strokeWidth="12" />
        <circle
          cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={offset} style={{ transition: "stroke-dashoffset 0.9s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-3xl font-extrabold text-navy" data-testid="demo-trust-score">{score}</span>
        <span className="text-xs text-slate-400">/ 100</span>
      </div>
    </div>
  );
};

export const ScamCheckDemo = () => {
  const { t } = useT();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const analyze = async () => {
    setError("");
    if (text.trim().length < 20) return setError(t("demo.minlen"));
    setLoading(true);
    setResult(null);
    try {
      const res = await api("/scam-check/demo", { method: "POST", body: JSON.stringify({ description: text.trim() }) });
      setResult(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const risk = result ? RISK[result.risk_level] || RISK.gemiddeld : null;

  return (
    <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
      <div className="overflow-hidden rounded-[2rem] border border-border bg-gradient-to-br from-slate-50 to-brandblue-light/40 p-6 shadow-sm sm:p-10">
        <div className="grid gap-8 lg:grid-cols-2">
          {/* left: input */}
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-navy px-3 py-1 text-xs font-semibold text-white">
              <Sparkles size={13} /> {t("demo.tag")}
            </span>
            <h2 className="mt-4 font-display text-3xl font-extrabold text-navy">{t("demo.title")}</h2>
            <p className="mt-2 text-slate-500">{t("demo.subtitle")}</p>

            <Textarea
              data-testid="demo-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("demo.placeholder")}
              className="mt-5 min-h-[160px] rounded-2xl border-border bg-white"
            />
            {error && <p className="mt-2 text-sm text-danger" data-testid="demo-error">{error}</p>}
            <Button
              onClick={analyze}
              disabled={loading}
              data-testid="demo-analyze-btn"
              className="mt-4 w-full rounded-xl bg-brandblue py-6 text-base hover:bg-brandblue/90"
            >
              {loading ? (
                <><Loader2 className="mr-2 animate-spin" size={18} /> {t("demo.analyzing")}</>
              ) : (
                <><ShieldCheck className="mr-2" size={18} /> {t("demo.analyze")}</>
              )}
            </Button>
            {result && typeof result.remaining === "number" && (
              <p className="mt-2 text-center text-xs text-slate-400">{t("demo.remaining", { n: result.remaining })}</p>
            )}
          </div>

          {/* right: result */}
          <div className="flex items-center justify-center rounded-3xl bg-white p-6 shadow-sm ring-1 ring-border">
            {!result ? (
              <div className="text-center text-slate-400">
                <ShieldCheck size={48} className="mx-auto opacity-40" />
                <p className="mt-3 text-sm">{loading ? t("demo.analyzing") : t("demo.subtitle")}</p>
              </div>
            ) : (
              <div className="w-full text-center" data-testid="demo-result">
                <TrustRing score={result.trust_score ?? 50} color={risk.ring} />
                <div className="mt-3 text-xs uppercase tracking-wide text-slate-400">{t("demo.trust")}</div>
                <span className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-bold ${risk.chip}`}>
                  {result.risk_level === "laag" ? <ShieldCheck size={14} /> : <AlertTriangle size={14} />}
                  {t(risk.labelKey)}
                </span>
                <p className="mt-3 text-sm text-slate-600">{result.summary}</p>

                {result.red_flags?.length > 0 && (
                  <div className="mt-4 text-left">
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-400">{t("demo.redflags")}</div>
                    <ul className="mt-2 space-y-1.5">
                      {result.red_flags.slice(0, 4).map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-danger" /> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Button asChild variant="link" className="mt-4 text-brandblue">
                  <Link to="/register" data-testid="demo-register-cta">
                    {t("demo.cta")} <ArrowRight size={15} className="ml-1" />
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
